import { Check, Copy } from "lucide-react";
import { type ComponentProps, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

const COPY_FEEDBACK_MS = 2000;

type CopyButtonProps = {
  content: string;
  onCopy?: () => void;
} & ComponentProps<typeof Button>;

function CopyButton({
  content,
  onCopy,
  className,
  variant = "ghost",
  size = "icon",
  children,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    onCopy?.();
  }, [content, onCopy]);

  return (
    <Button
      aria-label={copied ? "Copied" : "Copy to clipboard"}
      className={cn("size-8", copied && "text-green-500", className)}
      disabled={copied}
      onClick={handleCopy}
      size={size}
      type="button"
      variant={variant}
      {...props}
    >
      {children ?? (
        <span className="relative flex size-4 items-center justify-center">
          <Copy
            className={cn(
              "absolute size-4 transition-all duration-200",
              copied ? "scale-0 opacity-0" : "scale-100 opacity-100"
            )}
          />
          <Check
            className={cn(
              "absolute size-4 transition-all duration-200",
              copied ? "scale-100 opacity-100" : "scale-0 opacity-0"
            )}
          />
        </span>
      )}
    </Button>
  );
}

export type { CopyButtonProps };
export { CopyButton };
